/**
 * Asset Creation UGC form (Jonathan 2026-05-21).
 *
 * Players "upload" an image through a fake system file picker — a folder tree
 * presented as the fictional drive `C:` — or take a "simulated live" photo
 * from a camera.
 *
 * The drive's folders and files are **not** hardcoded: they are enumerated at
 * *build time* from a real local folder, `src/drive/<Folder>/<file>`, via
 * Vite's `import.meta.glob`. Drop an image into that tree and it appears in
 * the picker (folder = subdirectory name, thumbnail = the real image). The
 * Camera Roll, by contrast, is session-local and still *procedural* — captures
 * are deterministic seeded thumbnails so "taking a photo" needs no real bytes.
 *
 * The chosen asset is persisted (localStorage) and surfaced as the player's
 * created asset (profile avatar, launcher badge).
 */

import Phaser from "phaser";

import { makeSeededRng } from "./seededRng";

export interface FakeFile {
  name: string;
  /** Seed for the procedural thumbnail (Camera Roll captures + the fallback
   *  used if a real drive image can't be rasterised). */
  seed: number;
  /** Build-time resolved URL of the real image, for drive files. Absent for
   *  simulated Camera Roll captures. */
  url?: string;
}

export interface FakeFolder {
  name: string;
  files: FakeFile[];
}

export interface UploadedAsset {
  source: "drive" | "camera";
  folder: string;
  name: string;
  seed: number;
  /** Real-image URL for drive picks, so the avatar/badge render the same photo
   *  the picker showed. Absent for camera captures. */
  url?: string;
}

const STORAGE_KEY = "roamling.asset";

/**
 * Build-time enumeration of the fake `C:` drive. Every image committed under
 * `src/drive/<Folder>/...` is picked up by Vite at build (and dev) time; the
 * map is `relativePath -> resolved URL`. Eager so the listing is a plain
 * synchronous structure, exactly like the old hardcoded tree — except now the
 * source of truth is the folder on disk.
 */
const DRIVE_GLOB = import.meta.glob("../drive/**/*.{svg,png,jpg,jpeg,webp,gif}", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

/** Tiny FNV-1a hash → 32-bit seed, so each drive filename has a stable
 *  procedural-thumbnail fallback even before its real image is loaded. */
function seedFromName(name: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Group the glob's flat path list into `{ folder, files }`, sorted for a
 *  stable on-screen order. The path segment directly under `drive/` is the
 *  folder; deeper nesting is joined with " / ". Files sitting directly in
 *  `drive/` (no subfolder) collect under "Files". */
function buildDriveTree(): FakeFolder[] {
  const byFolder = new Map<string, FakeFile[]>();
  for (const [path, url] of Object.entries(DRIVE_GLOB)) {
    const rel = path.split("/drive/")[1];
    if (!rel) continue;
    const segs = rel.split("/");
    const name = segs.pop()!;
    const folder = segs.length > 0 ? segs.join(" / ") : "Files";
    const files = byFolder.get(folder) ?? [];
    files.push({ name, seed: seedFromName(name), url });
    byFolder.set(folder, files);
  }
  return [...byFolder.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, files]) => ({
      name,
      files: files.sort((x, y) => x.name.localeCompare(y.name)),
    }));
}

const DRIVE_FOLDERS: FakeFolder[] = buildDriveTree();

export class AssetService {
  /** Session-local camera roll, newest first. */
  private static cameraRoll: FakeFile[] = [];
  private static captureCount = 0;
  private static cachedUploaded: UploadedAsset | null | undefined = undefined;

  /** Folders on the fake drive (enumerated from `src/drive/` at build time),
   *  plus the live, session-local Camera Roll. */
  static folders(): FakeFolder[] {
    return [...DRIVE_FOLDERS, { name: "Camera Roll", files: [...this.cameraRoll] }];
  }

  /** Phaser texture key for a real drive image at its loaded native size. */
  static driveBaseKey(url: string): string {
    return `drive:${url}`;
  }

  /** Queue every real drive image on a scene's loader. Call once from the
   *  global `BootScene.preload()` so any scene can render drive thumbnails
   *  synchronously afterwards (Phaser textures are game-wide). SVGs are
   *  rasterised at a fixed size; other formats load at native size. */
  static queueDriveImages(scene: Phaser.Scene): void {
    for (const folder of DRIVE_FOLDERS) {
      for (const file of folder.files) {
        if (!file.url) continue;
        const key = this.driveBaseKey(file.url);
        if (scene.textures.exists(key)) continue;
        if (file.url.toLowerCase().endsWith(".svg")) {
          scene.load.svg(key, file.url, { width: 144, height: 108 });
        } else {
          scene.load.image(key, file.url);
        }
      }
    }
  }

  /** True for the Camera Roll folder (renders a camera affordance). */
  static isCameraFolder(name: string): boolean {
    return name === "Camera Roll";
  }

  /** Take a "simulated live" photo — a fresh seeded snapshot pushed onto the
   *  camera roll. Returns the new file. */
  static capture(): FakeFile {
    this.captureCount += 1;
    const file: FakeFile = {
      name: `IMG_${String(this.captureCount).padStart(4, "0")}.png`,
      seed: (Date.now() ^ (this.captureCount * 0x9e3779b1)) >>> 0,
    };
    this.cameraRoll.unshift(file);
    return file;
  }

  static setUploaded(asset: UploadedAsset): void {
    this.cachedUploaded = asset;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(asset));
    } catch {
      return;
    }
  }

  static getUploaded(): UploadedAsset | null {
    if (this.cachedUploaded !== undefined) return this.cachedUploaded;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as UploadedAsset;
        if (parsed && typeof parsed.seed === "number") {
          this.cachedUploaded = parsed;
          return parsed;
        }
      }
    } catch {
      // fall through
    }
    this.cachedUploaded = null;
    return null;
  }

  /**
   * Generate (and cache) a deterministic procedural "photo" texture for a
   * seed. `camera` snapshots get a green-tinted "live" treatment + scanlines
   * so they read differently from drive files.
   */
  static thumbnailKey(
    scene: Phaser.Scene,
    seed: number,
    camera = false,
    w = 72,
    h = 54,
  ): string {
    const key = `asset:${seed >>> 0}:${camera ? "cam" : "img"}:${w}x${h}`;
    if (scene.textures.exists(key)) return key;
    const tex = scene.textures.createCanvas(key, w, h);
    if (!tex) return key;
    const ctx = tex.getContext();
    const rng = makeSeededRng(seed >>> 0);

    const skyPalettes = [
      ["#2a3d66", "#6b86b8"],
      ["#7a3b52", "#e0a060"],
      ["#1f4036", "#5fae7e"],
      ["#3a2a4d", "#a86fb0"],
      ["#5a3a1f", "#caa060"],
    ];
    const sky = skyPalettes[rng.intRange(0, skyPalettes.length)]!;
    // Sky gradient (two bands).
    ctx.fillStyle = sky[0]!;
    ctx.fillRect(0, 0, w, Math.floor(h * 0.55));
    ctx.fillStyle = sky[1]!;
    ctx.fillRect(0, Math.floor(h * 0.45), w, h);

    // Sun / moon.
    const sunX = rng.intRange(8, w - 8);
    const sunY = rng.intRange(6, Math.floor(h * 0.4));
    const sunR = rng.intRange(4, 9);
    ctx.fillStyle = rng.chance(0.5) ? "#f2d27a" : "#f4ead0";
    ctx.beginPath();
    ctx.arc(sunX, sunY, sunR, 0, Math.PI * 2);
    ctx.fill();

    // Foreground silhouettes.
    const groundY = Math.floor(h * 0.62);
    ctx.fillStyle = "#1a1a22";
    const blocks = rng.intRange(3, 7);
    for (let i = 0; i < blocks; i++) {
      const bw = rng.intRange(6, 16);
      const bx = rng.intRange(0, w - bw);
      const bh = rng.intRange(6, h - groundY);
      ctx.fillRect(bx, h - bh, bw, bh);
    }
    ctx.fillStyle = "#11111a";
    ctx.fillRect(0, h - 4, w, 4);

    if (camera) {
      // Live-snapshot treatment: green tint wash + scanlines.
      ctx.fillStyle = "rgba(80,200,120,0.16)";
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = "rgba(0,0,0,0.18)";
      for (let y = 0; y < h; y += 2) ctx.fillRect(0, y, w, 1);
    }

    tex.refresh();
    return key;
  }

  /**
   * Texture key for a real drive image rendered at `w×h` (contain-fit,
   * letterboxed on a dark field, nearest-neighbour to stay crisp at the
   * native 384×216 scale). Requires the base image to have been preloaded by
   * {@link queueDriveImages}; falls back to a procedural thumbnail otherwise.
   */
  static driveThumbnailKey(
    scene: Phaser.Scene,
    url: string,
    seedFallback: number,
    w: number,
    h: number,
  ): string {
    const base = this.driveBaseKey(url);
    if (!scene.textures.exists(base)) {
      return this.thumbnailKey(scene, seedFallback, false, w, h);
    }
    const key = `${base}:${w}x${h}`;
    if (scene.textures.exists(key)) return key;
    const tex = scene.textures.createCanvas(key, w, h);
    if (!tex) return base;
    const src = scene.textures.get(base).getSourceImage() as
      | HTMLImageElement
      | HTMLCanvasElement;
    const sw = src.width || w;
    const sh = src.height || h;
    const scale = Math.min(w / sw, h / sh);
    const dw = Math.max(1, Math.round(sw * scale));
    const dh = Math.max(1, Math.round(sh * scale));
    const ctx = tex.getContext();
    ctx.fillStyle = "#101014";
    ctx.fillRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(src, Math.floor((w - dw) / 2), Math.floor((h - dh) / 2), dw, dh);
    tex.refresh();
    return key;
  }

  /** Thumbnail key for any picker file: the real image for drive files, a
   *  procedural snapshot for Camera Roll captures. */
  static fileThumbnailKey(
    scene: Phaser.Scene,
    file: FakeFile,
    camera: boolean,
    w: number,
    h: number,
  ): string {
    return file.url
      ? this.driveThumbnailKey(scene, file.url, file.seed, w, h)
      : this.thumbnailKey(scene, file.seed, camera, w, h);
  }

  /** Thumbnail key for the persisted uploaded asset (avatar / badge). */
  static uploadedThumbnailKey(
    scene: Phaser.Scene,
    asset: UploadedAsset,
    w: number,
    h: number,
  ): string {
    return asset.url
      ? this.driveThumbnailKey(scene, asset.url, asset.seed, w, h)
      : this.thumbnailKey(scene, asset.seed, asset.source === "camera", w, h);
  }
}
